import { RevealCall } from "@/components/listing/RevealCall";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, MapPin, Phone, ShieldCheck, Calendar as CalIcon, ArrowLeft, ExternalLink as ArrowSquareOut, Coins as Coin, MessageCircle, Pencil, Trash2, QrCode, Printer, Heart } from "lucide-react";
import { api, API, priceLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import MapView from "@/components/MapView";
import ReviewSection from "@/components/ReviewSection";
import AuctionPanel from "@/components/AuctionPanel";
import ShareButton from "@/components/ShareButton";
import ComparePanel from "@/components/ComparePanel";
import DatePriceCalendar, { priceForDate } from "@/components/DatePriceCalendar";
import TrustBadge from "@/components/TrustBadge";
import InfluencerCard from "@/components/InfluencerCard";
import VideoPlayer from "@/components/VideoPlayer";
import FindPayCheckout from "@/components/FindPayCheckout";
import { useSeo } from "@/hooks/useSeo";
import { toast } from "sonner";
import { loginRedirect } from "@/components/Layout";

export default function ListingDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [scheduleAt, setScheduleAt] = useState("");
  const [notes, setNotes] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [address, setAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [fpOpen, setFpOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const { user } = useAuth();
  const { loc } = useUserLocation();
  const navigate = useNavigate();

  const load = () => api.get(`/listings/${id}`).then(r => setL(r.data));
  useEffect(() => { load(); }, [id]);

  // Slice b: Save state — universal /api/favorites/toggle (Slice a).
  useEffect(() => {
    if (!user || !l?.listing_id) return;
    api.get("/favorites/check", {
      params: { target_type: "listing", target_id: l.listing_id },
    }).then(r => setFavorited(!!r.data?.favorited)).catch(() => {});
  }, [user, l?.listing_id]);

  const toggleSave = async () => {
    if (!user) { loginRedirect(); return; }
    try {
      const r = await api.post("/favorites/toggle", {
        target_type: "listing", target_id: l.listing_id,
      });
      setFavorited(!!r.data?.favorited);
      toast.success(r.data?.favorited ? "Saved" : "Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // SEO — LocalBusiness JSON-LD + title + description per listing. Only fires once the
  // listing has loaded; otherwise renders app defaults.
  useSeo(l ? {
    title: `${l.title} · ${l.city || "India"}`,
    description: `${l.description?.slice(0, 150) || `${l.category} listing in ${l.city || "India"}.`} ${l.rating_avg ? `Rated ${l.rating_avg}★ on find2near.` : ""}`.trim(),
    canonical: `/listing/${l.listing_id}`,
    schema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": l.listing_id,
      name: l.title,
      description: l.description,
      image: l.images?.[0],
      priceRange: l.price ? `₹${l.price}` : undefined,
      address: l.address ? {
        "@type": "PostalAddress",
        streetAddress: l.address,
        addressLocality: l.city,
        addressCountry: "IN",
      } : undefined,
      geo: l.lat && l.lng ? { "@type": "GeoCoordinates", latitude: l.lat, longitude: l.lng } : undefined,
      aggregateRating: l.rating_count ? {
        "@type": "AggregateRating",
        ratingValue: l.rating_avg,
        reviewCount: l.rating_count,
      } : undefined,
      telephone: l.contact_masked ? undefined : l.owner_mobile,
    },
  } : { title: "Listing · find2near" });

  // Deep-link support: /listing/:id?qr=1 opens the storefront QR sticker modal
  // automatically once the listing loads AND the current user is the owner/admin.
  // Used by the Dashboard's QR-sticker button so owners jump straight to the print modal.
  useEffect(() => {
    if (!l || !user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("qr") === "1" && (user.user_id === l.owner_id || user.is_admin)) {
      setQrOpen(true);
    }
  }, [l, user]);

  // Load payment options for this listing's category
  const [payOpts, setPayOpts] = useState(null);
  const [ben, setBen] = useState(null);
  useEffect(() => {
    if (l?.category) {
      api.get("/payments/options", { params: { category: l.category } })
        .then(r => { setPayOpts(r.data); setPaymentMethod(r.data.default); })
        .catch(() => {});
      api.get(`/beneficiaries/for-listing/${l.listing_id}`)
        .then(r => setBen(r.data))
        .catch(() => setBen(null));
    }
  }, [l?.listing_id, l?.category]);

  if (!l) return <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 text-stone-500">Loading…</div>;

  const book = async () => {
    if (!user) { loginRedirect(); return; }
    if (!scheduleAt) { toast.error("Please pick a date & time"); return; }
    // META-1 · Route non-COD payments through the FindPay drawer first;
    // the /bookings call runs from inside `finalizeBooking` on payment success.
    if (paymentMethod !== "cod") { setFpOpen(true); return; }
    return finalizeBooking();
  };

  const finalizeBooking = async (paymentTxn = null) => {
    try {
      const r = await api.post("/bookings", {
        listing_id: l.listing_id,
        customer_lat: loc.lat,
        customer_lng: loc.lng,
        customer_address: address || loc.city || "My location",
        scheduled_at: new Date(scheduleAt).toISOString(),
        notes,
        payment_method: paymentMethod,
        findpay_txn_id: paymentTxn?.txn_id || null,
      });
      toast.success(paymentTxn ? "Paid & booked!" : "Booking created!");
      navigate(`/track/${r.data.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    }
  };

  const submitReview = async () => {
    if (!user) { loginRedirect(); return; }
    if (reviewBusy) return;
    if (!comment.trim()) { toast.error("Write a short review first"); return; }
    if (rating < 1 || rating > 5) { toast.error("Pick a star rating (1-5)"); return; }
    setReviewBusy(true);
    try {
      await api.post("/reviews", { listing_id: l.listing_id, rating, comment: comment.trim() });
      setComment("");
      await load();
      toast.success("Review posted");
    } catch (e) {
      // Surface the actual backend detail so users know WHY it failed
      // (404 Listing not found, 401 Not authenticated, 429 rate-limited, etc.)
      // instead of a useless generic "Could not post review".
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) {
        toast.error("Please sign in again to post a review");
        loginRedirect();
      } else if (status === 429) {
        toast.error("Too many reviews — please wait a moment and try again");
      } else if (status === 404) {
        toast.error(detail || "This listing is no longer available");
      } else if (detail) {
        toast.error(String(detail));
      } else if (!navigator.onLine) {
        toast.error("You're offline — please reconnect and try again");
      } else {
        toast.error("Could not post review — please try again");
      }
    } finally {
      setReviewBusy(false);
    }
  };

  const isAuction = l.sale_type === "auction" || l.sale_type === "buy_or_bid";

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-stone-600 mb-4" data-testid="back-btn"><ArrowLeft size={16} /> Back</button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 min-w-0">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden bg-stone-100 border border-stone-200" data-testid="hero-media">
            {l.video_url && galleryIdx === -1 ? (
              <VideoPlayer url={l.video_url} className="!rounded-none" testid="listing-video" />
            ) : (
              (l.images?.[Math.max(0, galleryIdx)] || l.images?.[0]) && (
                <img
                  loading="lazy"
                  src={l.images[Math.max(0, galleryIdx)] || l.images[0]}
                  alt=""
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              )
            )}
          </div>

          {/* Gallery thumbnails — visible when >1 image OR when a video is present */}
          {((l.images?.length || 0) > 1 || l.video_url) && (
            <div
              className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
              data-testid="gallery-thumbs"
            >
              {l.video_url && (
                <button
                  type="button"
                  onClick={() => setGalleryIdx(-1)}
                  className={`shrink-0 relative w-24 h-16 rounded-lg overflow-hidden bg-stone-900 grid place-items-center text-white ${
                    galleryIdx === -1 ? "ring-2 ring-[#E05E36]" : "ring-1 ring-stone-200 hover:ring-stone-300"
                  }`}
                  data-testid="thumb-video"
                  aria-label="Play video"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  <span className="absolute bottom-0 left-0 right-0 text-[9px] font-bold uppercase tracking-wider bg-black/60 text-white text-center py-0.5">Video</span>
                </button>
              )}
              {(l.images || []).map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  type="button"
                  onClick={() => setGalleryIdx(i)}
                  className={`shrink-0 w-24 h-16 rounded-lg overflow-hidden ${
                    galleryIdx === i ? "ring-2 ring-[#E05E36]" : "ring-1 ring-stone-200 hover:ring-stone-300"
                  }`}
                  data-testid={`thumb-image-${i}`}
                  aria-label={`Photo ${i + 1}`}
                >
                  <img loading="lazy" src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{l.category}</div>
              <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900 mt-1">{l.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-stone-600">
                <div className="flex items-center gap-1"><MapPin size={14} /> {l.city}</div>
                <div className="flex items-center gap-1"><Star size={14} className="fill-[#DDA77B] text-[#DDA77B]" /> {l.rating_avg} ({l.rating_count})</div>
                {l.verified && <div className="inline-flex items-center gap-1 text-[10px] font-bold text-[#2D5A27] bg-[#2D5A27]/10 px-2 py-1 rounded-full"><ShieldCheck size={12} /> Verified</div>}
                {l.experience_years ? <div>{l.experience_years} yrs exp</div> : null}
              </div>
            </div>
            <ShareButton
              url={`/listing/${l.listing_id}`}
              title={l.title}
              text={`Check out "${l.title}" on Find2Near — ${l.city}. ${priceLabel(l.price, l.price_unit)}`}
              variant="chip"
              label="Share"
              testIdSuffix="listing"
            />
            {/* Owner (or admin) controls — Edit + Delete */}
            {user && (user.user_id === l.owner_id || user.is_admin) && !l.external && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/edit/${l.listing_id}`)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-300 hover:border-[#2D5A27] hover:text-[#2D5A27] text-sm font-bold text-stone-700 px-3.5 py-1.5 transition-colors"
                  data-testid="listing-edit-btn"
                >
                  <Pencil size={13} /> Edit
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm("Delete this listing permanently? This cannot be undone.")) return;
                    try {
                      await api.delete(`/listings/${l.listing_id}`);
                      toast.success("Listing deleted.");
                      navigate("/dashboard");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Delete failed");
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-300 hover:border-red-500 hover:text-red-600 text-sm font-bold text-stone-700 px-3.5 py-1.5 transition-colors"
                  data-testid="listing-delete-btn"
                >
                  <Trash2 size={13} /> Delete
                </button>
                <button
                  onClick={() => setQrOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-300 hover:border-[#E05E36] hover:text-[#E05E36] text-sm font-bold text-stone-700 px-3.5 py-1.5 transition-colors"
                  data-testid="listing-qr-btn"
                >
                  <QrCode size={13} /> QR sticker
                </button>
              </div>
            )}
            {!isAuction && <div className="heading text-2xl md:text-3xl font-extrabold text-[#E05E36]">{priceLabel(l.price, l.price_unit)}</div>}
          </div>

          <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6">
            <div className="heading font-bold text-lg mb-2">About</div>
            <p className="text-stone-700">{l.description}</p>
            {l.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {l.tags.map(t => <span key={t} className="text-xs font-semibold px-3 py-1 bg-stone-100 rounded-full">#{t}</span>)}
              </div>
            )}
          </div>

          <InfluencerCard
            listing={l}
            isOwner={!!user && (user.user_id === l.owner_id || user.is_admin)}
            onSaved={(next) => setL(prev => prev ? ({ ...prev, extra: { ...(prev.extra || {}), influencer: next } }) : prev)}
          />

          {/* Movie ticket-booking kit — links buyers straight to BookMyShow/Paytm/Insider.
              We fetch on-demand only for category=movie so non-movie pages stay lean. */}
          {l.category === "movie" && <MovieTicketKit listingId={l.listing_id} />}

          {/* Person profile card — shown for matrimonial / dating / job categories when
              the owner supplied identity fields via the Post Listing form. */}
          {["matrimonial", "dating", "job"].includes(l.category) && l.extra && (l.extra.candidate_name || l.extra.dob || l.extra.contact_no) && (
            <div className="mt-6 bg-white border-2 border-[#2D5A27]/20 rounded-2xl p-6" data-testid="person-profile-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A27]" />
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#2D5A27]">
                  {l.category === "matrimonial" ? "Matrimonial profile" : l.category === "dating" ? "Dating profile · 18+" : "Job seeker profile"}
                </span>
              </div>

              {/* Avatar + summary row */}
              <div className="flex items-start gap-4 mb-5">
                {l.images?.[0] ? (
                  <img
                    src={l.images[0]}
                    alt={l.extra.candidate_name || "Profile photo"}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-[#2D5A27]/30 shadow-sm shrink-0"
                    data-testid="profile-avatar"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#2D5A27]/10 grid place-items-center text-[#2D5A27] shrink-0 text-2xl font-extrabold" data-testid="profile-avatar-placeholder">
                    {(l.extra.candidate_name || l.title || "?").trim().charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {l.extra.candidate_name && (
                    <div className="heading text-xl font-extrabold text-stone-900" data-testid="profile-name-hero">
                      {l.extra.candidate_name}
                    </div>
                  )}
                  <div className="text-sm text-stone-500 mt-0.5">{l.city}{l.extra.contact_no ? " · verified contact" : ""}</div>
                </div>
              </div>

              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {l.extra.candidate_name && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Full Name</dt>
                    <dd className="mt-1 text-stone-900 font-semibold" data-testid="profile-name">{l.extra.candidate_name}</dd>
                  </div>
                )}
                {l.extra.dob && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Date of Birth · Age</dt>
                    <dd className="mt-1 text-stone-900 font-semibold" data-testid="profile-dob">
                      {(() => {
                        try {
                          const d = new Date(l.extra.dob);
                          const now = new Date();
                          let age = now.getFullYear() - d.getFullYear();
                          if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
                          return `${d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })} · ${age} yrs`;
                        } catch { return l.extra.dob; }
                      })()}
                    </dd>
                  </div>
                )}
                {l.extra.contact_no && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Contact</dt>
                    <dd className="mt-1">
                      <a
                        href={`tel:${l.extra.contact_no}`}
                        className="inline-flex items-center gap-2 text-[#E05E36] font-bold hover:underline"
                        data-testid="profile-contact"
                      >
                        <Phone size={14} /> {l.extra.contact_no}
                      </a>
                    </dd>
                  </div>
                )}
                {l.extra.annual_income_inr > 0 && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Annual Income</dt>
                    <dd className="mt-1 text-stone-900 font-semibold" data-testid="profile-annual-income">
                      ₹{Number(l.extra.annual_income_inr).toLocaleString("en-IN")}
                    </dd>
                  </div>
                )}
                {l.extra.family_income_inr > 0 && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Family Income</dt>
                    <dd className="mt-1 text-stone-900 font-semibold" data-testid="profile-family-income">
                      ₹{Number(l.extra.family_income_inr).toLocaleString("en-IN")}
                    </dd>
                  </div>
                )}
              </dl>
              {l.extra.family_profile && (
                <div className="mt-5 pt-5 border-t border-stone-200">
                  <dt className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Family Profile</dt>
                  <dd className="mt-1.5 text-stone-700 leading-relaxed whitespace-pre-line" data-testid="profile-family-profile">
                    {l.extra.family_profile}
                  </dd>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6">
            <div className="heading font-bold text-lg mb-3">Location</div>
            <MapView center={[l.lat, l.lng]} markers={[{ key: l.listing_id, lat: l.lat, lng: l.lng, label: "•", popup: l.title }]} height={320} zoom={14} />
            <div className="mt-3 text-sm text-stone-600">{l.address}, {l.city}</div>
          </div>

          {/* Reviews */}
          <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6">
            <div className="heading font-bold text-lg mb-4">Reviews ({l.reviews?.length || 0})</div>
            <div className="flex items-center gap-2 mb-3">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setRating(n)} data-testid={`rate-${n}`}>
                  <Star size={22} className={n <= rating ? "fill-[#DDA77B] text-[#DDA77B]" : "text-[#DDA77B]"} />
                </button>
              ))}
            </div>
            <Textarea placeholder="Share your experience…" value={comment} onChange={e => setComment(e.target.value)} data-testid="review-comment" />
            <Button
              onClick={submitReview}
              disabled={reviewBusy || !comment.trim()}
              className="mt-3 rounded-full bg-[#2D5A27] hover:bg-[#24491f] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="submit-review"
            >
              {reviewBusy ? "Posting…" : "Post review"}
            </Button>

            <div className="mt-6 space-y-4">
              {l.reviews?.map(r => (
                <div key={r.review_id} className="border-t border-stone-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-stone-900">{r.reviewer_name}</div>
                    <div className="flex items-center gap-1 text-xs"><Star size={12} className="fill-[#DDA77B] text-[#DDA77B]" /> {r.rating}</div>
                  </div>
                  <div className="text-sm text-stone-600 mt-1">{r.comment}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Cross-platform comparison — only for categories where users typically shop around */}
          {["job","matrimonial","hotel","flight","train","bus","movie","restaurant","cab","property","marriage_hall","party_hall"].includes(l.category) && (
            <ComparePanel
              category={l.category}
              query={l.title.split("·")[0].trim()}
              city={l.city}
              price={l.price}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 min-w-0">
          <div className="lg:sticky lg:top-24 bg-white border border-stone-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E05E36] text-white grid place-items-center font-bold">
                {l.owner_picture ? <img loading="lazy" src={l.owner_picture} className="w-full h-full rounded-full object-cover" alt="" /> : l.owner_name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{l.owner_name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-stone-500">Provider</span>
                  {l.owner_trust && <TrustBadge trust={l.owner_trust} size="xs" />}
                </div>
              </div>
              <div
                className="flex items-center gap-2 shrink-0"
                data-testid="viewer-actions"
              >
                <button
                  onClick={toggleSave}
                  className={`w-10 h-10 rounded-full grid place-items-center transition-colors ${
                    favorited
                      ? "bg-rose-500 hover:bg-rose-600 text-white"
                      : "bg-white border border-stone-300 hover:border-rose-400 text-stone-500 hover:text-rose-500"
                  }`}
                  data-testid="save-listing-btn"
                  data-favorited={favorited ? "true" : "false"}
                  aria-label={favorited ? "Remove from Saved" : "Save"}
                  title={favorited ? "Saved — tap to remove" : "Save this listing"}
                >
                  <Heart size={18} fill={favorited ? "currentColor" : "none"} />
                </button>
                <RevealCall listing={l} compact testid="call-provider-btn"
                            className="w-10 h-10 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white grid place-items-center" />
                <button
                  onClick={async () => {
                    if (!user) { loginRedirect(); return; }
                    try {
                      const r = await api.post("/conversations/start", { listing_id: l.listing_id });
                      navigate(`/messages/${r.data.conv_id}`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not start chat");
                    }
                  }}
                  className="w-10 h-10 rounded-full bg-[#2D5A27] hover:bg-[#24491f] text-white grid place-items-center"
                  data-testid="contact-provider-btn"
                  title="Message provider"
                  aria-label="Message provider"
                >
                  <MessageCircle size={18} />
                </button>
              </div>
            </div>

            {isAuction ? (
              <AuctionPanel listing={l} onUpdate={load} />
            ) : l.category === "loan" ? (
              <LoanApplyPanel listing={l} onApplied={load} />
            ) : l.category === "vacation" ? (
              <VacationPackagePanel listing={l} onBook={book} />
            ) : (l.category === "hotel" || l.category === "marriage_hall") && l.extra?.date_pricing ? (
              <DatePricedBookingPanel listing={l} onBook={book} />
            ) : l.category === "job" || l.category === "matrimonial" ? (
              <div className="mt-4 text-sm text-stone-600">
                {l.category === "job" ? "Interested in this vacancy?" : "Interested in this profile?"} Contact the provider directly.
                <RevealCall listing={l} testid="contact-mobile" className="mt-3 flex items-center gap-2 font-semibold text-stone-900" />
              </div>
            ) : l.category === "yojna" ? (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-[#2D5A27]/10 rounded-xl text-sm text-stone-700">
                  Official government scheme. Visit the portal to check eligibility & apply.
                </div>
                {l.extra?.official_link && (
                  <a href={l.extra.official_link} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-[#2D5A27] hover:bg-[#24491f] text-white font-bold px-5 py-3 rounded-full" data-testid="yojna-official-link">
                    <ArrowSquareOut size={18} /> Visit official page
                  </a>
                )}
              </div>
            ) : l.category === "commodity" ? (
              <div className="mt-4 space-y-3">
                <div className="p-4 rounded-2xl bg-gradient-to-br from-[#DDA77B]/25 to-[#E05E36]/10 border border-[#DDA77B]/40 relative overflow-hidden">
                  {/* LIVE pulse badge — top-right */}
                  <span
                    className={`absolute top-3 right-3 inline-flex items-center gap-1.5 text-white text-[9px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full ${l.price_is_live ? "bg-[#2D5A27]" : "bg-stone-600"}`}
                    data-testid="commodity-live-badge"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${l.price_is_live ? "bg-[#DDA77B] animate-pulse" : "bg-stone-300"}`} />
                    {l.price_is_live ? "Live" : l.price_status === "DELAYED" ? "Delayed" : "Unavailable"}
                  </span>
                  <div className="flex items-center gap-1 text-xs font-bold text-stone-600 uppercase tracking-widest pr-24"><Coin size={14} /> {l.price_is_live ? "Live estimate · GST excl." : l.price_status === "DELAYED" ? "Delayed estimate" : "Seller price"}</div>
                  <div className="heading text-3xl font-extrabold text-stone-900 mt-1" data-testid="commodity-live-price">₹{Number(l.live_price || l.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                  <div className="text-xs text-stone-600">per {l.extra?.unit_label || "gram"} · refreshes on every load</div>
                  {l.market_base && (
                    <div className="text-[11px] text-stone-500 mt-2 flex flex-wrap items-center gap-x-2">
                      <span>Spot ₹{Number(l.market_base).toLocaleString("en-IN", { maximumFractionDigits: 2 })}/g</span>
                      <span className="text-stone-300">·</span>
                      <span>Dealer margin {l.extra?.margin_pct}%</span>
                      {l.extra?.commodity_type && (
                        <>
                          <span className="text-stone-300">·</span>
                          <span className="uppercase tracking-wider font-semibold text-[#8B5E34]">{l.extra.commodity_type}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <RevealCall listing={l} testid="commodity-call" label="Call dealer"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#E05E36] hover:bg-[#c94e2a] text-white font-bold px-5 py-3 rounded-full" />
              </div>
            ) : (
              <>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-stone-600">Schedule</label>
                    <Input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="mt-1" data-testid="schedule-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-stone-600">Your address</label>
                    <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Flat, area…" className="mt-1" data-testid="address-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-stone-600">Notes</label>
                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything the provider should know?" className="mt-1" data-testid="notes-input" />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-stone-200 pt-4">
                  <div className="text-sm text-stone-500">Charges</div>
                  <div className="heading font-extrabold text-[#E05E36]">{priceLabel(l.price, l.price_unit)}</div>
                </div>
                <div className="flex items-center justify-between mt-1 text-xs">
                  <div className="text-stone-500 flex items-center gap-1">
                    Platform fee
                    <span title="₹1 collected to route funds only to verified genuine accounts." className="w-3.5 h-3.5 rounded-full bg-stone-200 text-stone-600 text-[9px] font-bold grid place-items-center cursor-help">?</span>
                  </div>
                  <div className="font-bold text-stone-700">+ ₹1</div>
                </div>
                <div className="flex items-center justify-between mt-1 pt-1 border-t border-dashed border-stone-200">
                  <div className="text-xs font-bold text-stone-800">Total</div>
                  <div className="heading font-extrabold text-stone-900" data-testid="booking-total">₹{(Number(l.price || 0) + 1).toLocaleString("en-IN")}</div>
                </div>

                {/* Payment method selection - category aware */}
                <div className="mt-3">
                  <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">Payment method</div>
                  {(() => {
                    const allLabels = {
                      advance: { label: "Pay advance", sub: "Full now" },
                      partial: { label: `Partial pay`, sub: payOpts?.partial_deposit_pct ? `${payOpts.partial_deposit_pct}% now + rest COD` : "Deposit now" },
                      pay_later: { label: "Pay later", sub: "After service" },
                      cod: { label: "Cash on delivery", sub: "On delivery" },
                    };
                    const allowed = payOpts?.allowed || ["pay_later", "cod", "advance"];
                    return (
                      <div className={`grid gap-2 ${allowed.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                        {allowed.map(key => {
                          const opt = allLabels[key];
                          const active = paymentMethod === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setPaymentMethod(key)}
                              className={`text-xs font-bold rounded-xl px-2 py-2 border transition-colors ${active ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
                              data-testid={`payment-${key}`}
                            >
                              <div>{opt.label}</div>
                              <div className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-stone-500"}`}>{opt.sub}</div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {paymentMethod === "partial" && payOpts?.partial_deposit_pct && l.price > 0 && (
                    <div className="mt-2 text-[11px] text-stone-600 bg-stone-50 rounded-xl p-2">
                      Deposit now: <b className="text-[#E05E36]">₹{Math.round(l.price * payOpts.partial_deposit_pct / 100).toLocaleString("en-IN")}</b>
                      <span className="text-stone-500"> · Remaining on delivery: ₹{Math.round(l.price * (100 - payOpts.partial_deposit_pct) / 100).toLocaleString("en-IN")}</span>
                    </div>
                  )}
                </div>

                <Button onClick={book} className="w-full mt-4 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2" data-testid="book-btn">
                  <CalIcon size={16} />
                  {paymentMethod === "advance" ? `Book & pay ₹${Number(l.price).toLocaleString("en-IN")}` :
                   paymentMethod === "partial" && payOpts ? `Book & pay ₹${Math.round(l.price * payOpts.partial_deposit_pct / 100).toLocaleString("en-IN")} deposit` :
                   "Book now"}
                </Button>
                <div className="text-[11px] text-stone-500 mt-2 text-center">Live map tracking begins after provider confirms.</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* QR sticker dialog — printable poster for the shop owner */}
      {qrOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 print:static print:bg-white print:p-0"
          onClick={() => setQrOpen(false)}
          data-testid="qr-sticker-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full print:shadow-none print:max-w-none print:rounded-none"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 print:p-10 flex flex-col items-center text-center" id="qr-sticker-printable">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#2D5A27]">Find2Near</div>
              <div className="heading text-2xl font-extrabold text-stone-900 mt-1 line-clamp-2">{l.title}</div>
              <div className="text-xs text-stone-500 mt-0.5">{l.city}{l.address ? ` · ${l.address}` : ""}</div>

              <div className="my-5 rounded-2xl border-4 border-stone-900 p-3 bg-white">
                <img
                  src={`${API}/listings/${l.listing_id}/qr.svg?ts=${l.updated_at || l.created_at || ""}`}
                  alt={`QR code for ${l.title}`}
                  className="w-56 h-56 print:w-72 print:h-72"
                  data-testid="qr-sticker-img"
                />
              </div>

              <div className="text-sm font-bold text-stone-900">Scan to view + review</div>
              <div className="text-xs text-stone-500 mt-1 max-w-xs">
                Print this and stick it near your entrance. Every scan brings a new customer.
              </div>
              <div className="text-[10px] text-stone-400 mt-3 font-mono truncate max-w-full">
                find2near.app/listing/{l.listing_id}
              </div>
            </div>

            <div className="border-t border-stone-100 p-3 flex justify-between gap-2 print:hidden">
              <Button variant="outline" onClick={() => setQrOpen(false)} className="rounded-full" data-testid="qr-sticker-close">
                Close
              </Button>
              <div className="flex gap-2">
                <a
                  href={`${API}/listings/${l.listing_id}/qr.svg`}
                  download={`find2near-${l.listing_id}.svg`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-bold px-4 py-2 transition-colors"
                  data-testid="qr-sticker-download"
                >
                  <QrCode size={14} /> Download SVG
                </a>
                <Button
                  onClick={() => window.print()}
                  className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-1.5"
                  data-testid="qr-sticker-print"
                >
                  <Printer size={14} /> Print
                </Button>
              </div>
            </div>
          </div>

          {/* Print-only CSS: hide chrome so only the sticker is printed. */}
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              #qr-sticker-printable, #qr-sticker-printable * { visibility: visible !important; }
              #qr-sticker-printable { position: absolute !important; inset: 0 !important; margin: auto !important; }
            }
          `}</style>
        </div>
      )}
      {/* META-2 · verified reviews for this listing.
          Verifier: at least one conversation with the owner (contact-first). */}
      {l?.listing_id && (
        <ReviewSection entityType="listing" entityId={l.listing_id} entityLabel="listing" />
      )}
      {fpOpen && l && (
        <FindPayCheckout
          open={fpOpen}
          onOpenChange={setFpOpen}
          merchantId="fpm_platform"
          amountPaise={Math.max(1, l.price || 100) * 100}
          currency="INR"
          description={`${l.category} · ${l.title || l.name || "Booking"}`}
          context={{ kind: "listing_book", listing_id: l.listing_id, category: l.category }}
          onSuccess={(txn) => { setFpOpen(false); finalizeBooking(txn); }}
        />
      )}
    </div>
  );
}

// -- Vacation Package Panel -------------------------------------------------
function VacationPackagePanel({ listing, onBook }) {
  const includes = listing.extra?.package_includes || [
    "Return transport", "Hotel accommodation", "Daily breakfast", "Sightseeing", "24x7 support"
  ];
  const dest = listing.extra?.destination;
  const origin = listing.extra?.origin_city || listing.city;
  const days = listing.extra?.duration_days;
  const hotelPct = listing.extra?.hotel_component_pct ?? 45;
  const transportPct = listing.extra?.transport_component_pct ?? 40;
  const otherPct = listing.extra?.other_component_pct ?? 15;
  const basePrice = Number(listing.price || 0);
  const dp = listing.extra?.date_pricing;

  const [dateState, setDateState] = useState(() => {
    const iso = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    return { startDate: iso, ...priceForDate(basePrice, dp, iso) };
  });
  const { finalPrice, appliedPct, appliedLabel } = dateState;

  return (
    <div className="mt-4 space-y-3" data-testid="vacation-package-panel">
      <div className="rounded-2xl bg-gradient-to-br from-sky-50 via-white to-emerald-50 border border-sky-200 p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-sky-700 flex items-center gap-1.5">
          <CalIcon size={12} /> Package
        </div>
        <div className="mt-1 heading text-lg font-extrabold text-stone-900">
          {origin} <span className="text-[#E05E36]">→</span> {dest || "Destination"}
        </div>
        {days && <div className="text-xs text-stone-600">{days} days · {days - 1} nights</div>}
      </div>

      <DatePriceCalendar
        basePrice={basePrice}
        datePricing={dp}
        onChange={setDateState}
        labelPrefix="Start date"
        testId="vacation-date"
      />

      <div className="rounded-xl border border-stone-200 p-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">What&apos;s included</div>
        <ul className="space-y-1.5 text-sm text-stone-800" data-testid="vacation-includes">
          {includes.map((it, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#2D5A27] mt-0.5">✓</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-stone-200 p-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Price breakdown</div>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center justify-between"><span className="text-stone-600">Base package</span><span className="font-bold">₹{basePrice.toLocaleString("en-IN")}</span></div>
          <div className="flex items-center justify-between"><span className="text-stone-600">Transport ({transportPct}%)</span><span>₹{Math.round(basePrice * transportPct / 100).toLocaleString("en-IN")}</span></div>
          <div className="flex items-center justify-between"><span className="text-stone-600">Hotel &amp; meals ({hotelPct}%)</span><span>₹{Math.round(basePrice * hotelPct / 100).toLocaleString("en-IN")}</span></div>
          <div className="flex items-center justify-between"><span className="text-stone-600">Sightseeing &amp; extras ({otherPct}%)</span><span>₹{Math.round(basePrice * otherPct / 100).toLocaleString("en-IN")}</span></div>
          {appliedPct !== 0 && (
            <div className="flex items-center justify-between pt-1 border-t border-dashed border-stone-200">
              <span className={appliedPct > 0 ? "text-rose-700" : "text-emerald-700"}>Date adjustment ({appliedLabel})</span>
              <span className={`font-bold ${appliedPct > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {appliedPct > 0 ? "+" : ""}₹{Math.abs(finalPrice - basePrice).toLocaleString("en-IN")}
              </span>
            </div>
          )}
          <div className="border-t border-stone-200 pt-2 flex items-center justify-between">
            <span className="font-bold text-stone-900">You pay</span>
            <span className="heading text-xl font-extrabold text-[#E05E36]" data-testid="vacation-final-price">₹{finalPrice.toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>

      <Button
        onClick={onBook}
        className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2"
        data-testid="book-package-btn"
      >
        <CalIcon size={16} /> Book this package
      </Button>
      <div className="text-[10px] text-stone-500 text-center">
        Flight/train + hotel components are reserved together — one booking, one confirmation.
      </div>
    </div>
  );
}

// -- Hotel / Marriage-Hall Date-Priced Booking Panel ---------------------------
function DatePricedBookingPanel({ listing, onBook }) {
  const basePrice = Number(listing.price || 0);
  const dp = listing.extra?.date_pricing;
  const isHotel = listing.category === "hotel";
  const [dateState, setDateState] = useState(() => {
    const iso = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    return { startDate: iso, ...priceForDate(basePrice, dp, iso) };
  });
  const nights = (() => {
    if (!isHotel || !dateState.endDate) return 1;
    try {
      const d1 = new Date(dateState.startDate).getTime();
      const d2 = new Date(dateState.endDate).getTime();
      return Math.max(1, Math.round((d2 - d1) / 86400000));
    } catch { return 1; }
  })();
  const total = dateState.finalPrice * nights;

  return (
    <div className="mt-4 space-y-3" data-testid="date-priced-panel">
      <DatePriceCalendar
        basePrice={basePrice}
        datePricing={dp}
        showRange={isHotel}
        onChange={setDateState}
        labelPrefix={isHotel ? "Stay dates" : "Event date"}
        testId={isHotel ? "hotel-date" : "hall-date"}
      />
      <div className="rounded-xl border border-stone-200 p-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Price</div>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center justify-between"><span className="text-stone-600">Base rate</span><span>₹{basePrice.toLocaleString("en-IN")} {isHotel ? "/ night" : "/ day"}</span></div>
          {dateState.appliedPct !== 0 && (
            <div className="flex items-center justify-between">
              <span className={dateState.appliedPct > 0 ? "text-rose-700" : "text-emerald-700"}>{dateState.appliedLabel}</span>
              <span className={`font-bold ${dateState.appliedPct > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {dateState.appliedPct > 0 ? "+" : ""}{dateState.appliedPct}%
              </span>
            </div>
          )}
          {isHotel && nights > 1 && (
            <div className="flex items-center justify-between text-xs text-stone-500">
              <span>× {nights} nights</span>
              <span>₹{dateState.finalPrice.toLocaleString("en-IN")} × {nights}</span>
            </div>
          )}
          <div className="border-t border-stone-200 pt-2 flex items-center justify-between">
            <span className="font-bold text-stone-900">Total</span>
            <span className="heading text-xl font-extrabold text-[#E05E36]" data-testid="hotel-total-price">₹{total.toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>
      <Button
        onClick={onBook}
        className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2"
        data-testid="book-date-priced-btn"
      >
        <CalIcon size={16} /> Book now
      </Button>
    </div>
  );
}

// -- Loan Apply Panel ---------------------------------------------------------
function LoanApplyPanel({ listing, onApplied }) {
  const [form, setForm] = useState({
    loan_type: (listing.tags && listing.tags.find(t => /loan|personal|home|gold|business|vehicle|education/i.test(t))) || "gold",
    amount_requested_inr: 100000,
    tenure_months: 24,
    asset_type: "",
    asset_value_inr: "",
    monthly_income_inr: "",
    employment_type: "salaried",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const rate = listing.extra?.interest_rate_pct;
  const emi = (() => {
    if (!rate) return null;
    const r = rate / 12 / 100;
    const n = Number(form.tenure_months);
    const p = Number(form.amount_requested_inr);
    if (!r || !n || !p) return null;
    return Math.round(p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1));
  })();

  const apply = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/loans/apply", {
        lender_listing_id: listing.listing_id,
        loan_type: form.loan_type,
        amount_requested_inr: Number(form.amount_requested_inr),
        tenure_months: Number(form.tenure_months),
        asset_type: form.asset_type || null,
        asset_value_inr: form.asset_value_inr ? Number(form.asset_value_inr) : null,
        monthly_income_inr: form.monthly_income_inr ? Number(form.monthly_income_inr) : null,
        employment_type: form.employment_type,
      });
      setDone(r.data);
      toast.success("Application submitted");
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-4 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900" data-testid="loan-done">
        <div className="font-bold">Application received</div>
        <div className="text-xs mt-1">Ref #{done.application_id.slice(-6).toUpperCase()} · Our partner team from <b>{done.lender_name}</b> will call you shortly.</div>
        {emi != null && <div className="text-xs mt-2">Approx EMI: <b>₹{emi.toLocaleString("en-IN")}</b> / month for {done.tenure_months} months at {rate}% p.a.</div>}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="loan-apply-panel">
      <div className="rounded-xl bg-gradient-to-br from-[#DDA77B]/20 to-[#2D5A27]/10 border border-[#DDA77B]/30 p-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#8B5E34]">Registered lender</div>
        <div className="text-sm font-bold text-stone-900 mt-1">{listing.extra?.regulator || "RBI-regulated"}</div>
        {rate && <div className="text-xs text-stone-700 mt-1">Interest rate: <b>{rate}% p.a.</b></div>}
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Amount (₹)</label>
        <Input type="number" value={form.amount_requested_inr} onChange={e => setForm(f => ({ ...f, amount_requested_inr: e.target.value }))} className="mt-1 rounded-xl" data-testid="loan-amount" />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Tenure (months)</label>
        <Input type="number" value={form.tenure_months} onChange={e => setForm(f => ({ ...f, tenure_months: e.target.value }))} className="mt-1 rounded-xl" data-testid="loan-tenure" />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Employment</label>
        <select value={form.employment_type} onChange={e => setForm(f => ({ ...f, employment_type: e.target.value }))} className="w-full mt-1 border border-stone-200 rounded-xl px-3 py-2 text-sm" data-testid="loan-employment">
          <option value="salaried">Salaried</option>
          <option value="self-employed">Self-employed</option>
          <option value="business">Business owner</option>
          <option value="student">Student</option>
        </select>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Monthly income (₹)</label>
        <Input type="number" value={form.monthly_income_inr} onChange={e => setForm(f => ({ ...f, monthly_income_inr: e.target.value }))} placeholder="Optional" className="mt-1 rounded-xl" data-testid="loan-income" />
      </div>

      {emi != null && (
        <div className="rounded-xl bg-white border border-stone-200 p-3 text-sm" data-testid="loan-emi-preview">
          <div className="text-stone-500 text-xs">Approx EMI</div>
          <div className="heading text-2xl font-extrabold text-[#E05E36]">₹{emi.toLocaleString("en-IN")}<span className="text-xs text-stone-500 font-normal"> / month</span></div>
          <div className="text-[11px] text-stone-500">Total ₹{(emi * form.tenure_months).toLocaleString("en-IN")} over {form.tenure_months} months</div>
        </div>
      )}

      <Button onClick={apply} disabled={submitting} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="loan-apply-btn">
        {submitting ? "Applying…" : `Apply for ₹${Number(form.amount_requested_inr).toLocaleString("en-IN")}`}
      </Button>
      <div className="text-[10px] text-stone-500 text-center">
        Regulated by RBI. By applying you consent to be contacted by the lender.
      </div>
    </div>
  );
}


// Movie ticket-booking deep-link kit — renders BookMyShow / Paytm / Insider / Google buttons.
function MovieTicketKit({ listingId }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!listingId) return;
    api.get(`/movies/${listingId}/ticket-links`)
       .then(r => setData(r.data))
       .catch(() => setData(null));
  }, [listingId]);

  if (!data) return null;
  const { bookmyshow, paytm, insider, google_showtimes } = data.links || {};
  const btn = "inline-flex items-center gap-1.5 rounded-full text-xs font-black uppercase tracking-widest px-4 min-h-[44px] text-white transition-colors";

  return (
    <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6" data-testid="movie-ticket-kit">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-1.5 rounded-full bg-[#E05E36]" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">Book tickets in {data.city || "your city"}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={bookmyshow} target="_blank" rel="noopener noreferrer"
           className={`${btn} bg-[#c4242b] hover:bg-[#a51e24]`}
           data-testid="movie-ticket-bookmyshow">BookMyShow</a>
        <a href={paytm} target="_blank" rel="noopener noreferrer"
           className={`${btn} bg-[#00baf2] hover:bg-[#0099cc]`}
           data-testid="movie-ticket-paytm">Paytm</a>
        <a href={insider} target="_blank" rel="noopener noreferrer"
           className={`${btn} bg-stone-900 hover:bg-stone-800`}
           data-testid="movie-ticket-insider">Insider</a>
        <a href={google_showtimes} target="_blank" rel="noopener noreferrer"
           className={`${btn} bg-emerald-700 hover:bg-emerald-800`}
           data-testid="movie-ticket-google">Showtimes</a>
      </div>
      <div className="text-[10px] text-stone-500 mt-3">
        Deep-links to the ticket portal — you complete the purchase on their site.
      </div>
    </div>
  );
}

